import { useState } from "react";
import {
    FileText,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Eye,
    EyeOff,
    ChevronDown,
    Info,
    MapPin,
    Clock,
    Code
} from "lucide-react";
import { getValidationStatusIcon } from "../../../utils/validationUtils.js";
import OfferDiagnosticsDisplay from "../shared/OfferDiagnosticsDisplay.jsx";
import ComponentAnalysisDisplay from "../shared/ComponentAnalysisDisplay.jsx";

export default function OffersValidation({ validation, companyId, companyName, pageUrl, onHideOffer, onUnhideOffer, hiddenOffers = [] }) {
    const [activeTab, setActiveTab] = useState('active');
    const [expandedItems, setExpandedItems] = useState(new Set());

    const toggleItem = (id) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const isOfferHidden = (offerText) => {
        return hiddenOffers.some(hidden =>
            hidden.offerText.toLowerCase().trim() === offerText.toLowerCase().trim()
        );
    };

    const activeMatches = (validation.matches || []).filter(match => !isOfferHidden(match.databaseOffer));
    const activeMismatches = (validation.mismatches || []).filter(mismatch => !isOfferHidden(mismatch.databaseOffer));
    const activeMissing = (validation.missing || []).filter(missing => !isOfferHidden(missing.databaseOffer));

    const totalExpected = activeMatches.length + activeMismatches.length + activeMissing.length;
    const totalHidden = hiddenOffers.length;

    const getScoreColor = (score) => {
        if (score >= 90) return { gradient: 'from-emerald-500 to-emerald-600', text: 'text-emerald-600', lightBg: 'bg-emerald-50', border: 'border-emerald-200' };
        if (score >= 75) return { gradient: 'from-blue-500 to-blue-600', text: 'text-blue-600', lightBg: 'bg-blue-50', border: 'border-blue-200' };
        if (score >= 50) return { gradient: 'from-amber-500 to-amber-600', text: 'text-amber-600', lightBg: 'bg-amber-50', border: 'border-amber-200' };
        return { gradient: 'from-red-500 to-red-600', text: 'text-red-600', lightBg: 'bg-red-50', border: 'border-red-200' };
    };

    const scoreColor = getScoreColor(validation.score);

    return (
        <div className="space-y-4">
            {/* Compact Header with Stats */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                <div className={`bg-gradient-to-r ${scoreColor.gradient} px-4 py-4`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                                <FileText className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white">Offers Validation</h3>
                                <p className="text-xs text-white/90 mt-0.5">Database offer matching</p>
                            </div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                            <span className="text-2xl font-semibold text-white">
                                {validation.score}%
                            </span>
                        </div>
                    </div>
                </div>

                {/* Compact Stats Row */}
                <div className="grid grid-cols-4 gap-3 px-4 py-3 bg-gradient-to-b from-gray-50 to-white border-b border-gray-200">
                    <StatCard value={activeMatches.length} label="Perfect" color="emerald" />
                    <StatCard value={activeMismatches.length} label="Partial" color="amber" />
                    <StatCard value={activeMissing.length} label="Missing" color="red" />
                    <StatCard value={totalExpected} label="Total" color="slate" />
                </div>

                {/* Inline Tab Navigation */}
                <div className="px-4 py-2 flex gap-2 bg-white">
                    <button
                        onClick={() => setActiveTab('active')}
                        className={`flex-1 px-3 py-2 font-medium text-sm rounded-lg transition-all ${activeTab === 'active'
                                ? 'bg-slate-700 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        Active <span className="opacity-75">({totalExpected})</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('hidden')}
                        className={`flex-1 px-3 py-2 font-medium text-sm rounded-lg transition-all flex items-center justify-center gap-1.5 ${activeTab === 'hidden'
                                ? 'bg-slate-700 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <EyeOff className="w-3.5 h-3.5" />
                        Hidden <span className="opacity-75">({totalHidden})</span>
                    </button>
                </div>
            </div>

            {/* Active Tab Content */}
            {activeTab === 'active' && (
                <div className="space-y-5">
                    {activeMatches.length > 0 && (
                        <ValidationSection
                            title="Perfect Matches"
                            count={activeMatches.length}
                            icon={CheckCircle2}
                            iconColor="text-emerald-600"
                        >
                            {activeMatches.map((match, i) => (
                                <OfferCard
                                    key={`match-${i}`}
                                    id={`match-${i}`}
                                    type="success"
                                    expanded={expandedItems.has(`match-${i}`)}
                                    onToggle={() => toggleItem(`match-${i}`)}
                                    confidence={match.confidence}
                                    databaseOffer={match.databaseOffer}
                                    scrapedContent={match.scrapedContent}
                                    diagnostics={match.detailedBreakdown?.diagnostics}
                                    matchEvidence={match.matchEvidence}
                                    pageContext={match.pageContext}
                                    pageLocation={match.pageLocation}
                                    componentAnalysis={match.componentAnalysis}
                                    extractionMethod={match.extractionMethod}
                                />
                            ))}
                        </ValidationSection>
                    )}

                    {activeMismatches.length > 0 && (
                        <ValidationSection
                            title="Partial Matches"
                            count={activeMismatches.length}
                            icon={AlertTriangle}
                            iconColor="text-amber-600"
                            description="Found on page but with formatting differences or low keyword matches"
                        >
                            {activeMismatches.map((mismatch, i) => (
                                <OfferCard
                                    key={`mismatch-${i}`}
                                    id={`mismatch-${i}`}
                                    type="warning"
                                    expanded={expandedItems.has(`mismatch-${i}`)}
                                    onToggle={() => toggleItem(`mismatch-${i}`)}
                                    confidence={mismatch.confidence}
                                    databaseOffer={mismatch.databaseOffer}
                                    scrapedContent={mismatch.foundText || mismatch.scrapedContent}
                                    diagnostics={mismatch.detailedBreakdown?.diagnostics}
                                    componentAnalysis={mismatch.componentAnalysis}
                                    issue={mismatch.issue}
                                    onHide={companyId && pageUrl && onHideOffer ? () => onHideOffer(mismatch.databaseOffer, 'regular') : null}
                                />
                            ))}
                        </ValidationSection>
                    )}

                    {activeMissing.length > 0 && (
                        <ValidationSection
                            title="Missing Offers"
                            count={activeMissing.length}
                            icon={XCircle}
                            iconColor="text-red-600"
                            description="These offers were not detected on the scraped page"
                        >
                            {activeMissing.map((offer, i) => (
                                <MissingOfferCard
                                    key={`missing-${i}`}
                                    offer={offer}
                                    onHide={companyId && pageUrl && onHideOffer ? () => onHideOffer(offer.databaseOffer, 'regular') : null}
                                />
                            ))}
                        </ValidationSection>
                    )}
                </div>
            )}

            {/* Hidden Tab Content */}
            {activeTab === 'hidden' && (
                <div className="space-y-4">
                    {totalHidden === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                                <EyeOff className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-700 font-medium text-lg mb-2">No hidden offers</p>
                            <p className="text-sm text-gray-500 max-w-md mx-auto">
                                Use the hide button on missing or partial match offers to exclude them from validation
                            </p>
                        </div>
                    ) : (
                        <ValidationSection
                            title="Hidden Offers"
                            count={totalHidden}
                            icon={EyeOff}
                            iconColor="text-gray-600"
                            description="These offers are excluded from validation on this page"
                        >
                            {hiddenOffers.map((hidden, i) => (
                                <HiddenOfferCard
                                    key={`hidden-${i}`}
                                    hidden={hidden}
                                    onUnhide={onUnhideOffer ? () => onUnhideOffer(hidden.offerText) : null}
                                />
                            ))}
                        </ValidationSection>
                    )}
                </div>
            )}
        </div>
    );
}

// Stat Card Component - More Compact
function StatCard({ value, label, color }) {
    const colors = {
        emerald: 'text-emerald-600 bg-emerald-50',
        amber: 'text-amber-600 bg-amber-50',
        red: 'text-red-600 bg-red-50',
        slate: 'text-slate-700 bg-slate-50'
    };

    return (
        <div className="text-center">
            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${colors[color]} mb-1.5`}>
                <span className="text-xl font-semibold">{value}</span>
            </div>
            <div className="text-xs text-gray-600 font-medium">{label}</div>
        </div>
    );
}

// Validation Section Component - More Compact
function ValidationSection({ title, count, icon: Icon, iconColor, description, children }) {
    return (
        <div className="space-y-2.5">
            <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${iconColor}`} />
                <h4 className="text-sm font-semibold text-gray-900">
                    {title} <span className="text-gray-500">({count})</span>
                </h4>
            </div>
            {description && (
                <p className="text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded border border-gray-200">
                    {description}
                </p>
            )}
            <div className="space-y-2.5">
                {children}
            </div>
        </div>
    );
}

// Offer Card Component
function OfferCard({
    id,
    type,
    expanded,
    onToggle,
    confidence,
    databaseOffer,
    scrapedContent,
    diagnostics,
    matchEvidence,
    pageContext,
    pageLocation,
    componentAnalysis,
    extractionMethod,
    issue,
    onHide
}) {
    const colors = {
        success: {
            border: 'border-emerald-200',
            bg: 'bg-emerald-50',
            text: 'text-emerald-700',
            badge: 'bg-emerald-100 text-emerald-700 border-emerald-300'
        },
        warning: {
            border: 'border-amber-200',
            bg: 'bg-amber-50',
            text: 'text-amber-700',
            badge: 'bg-amber-100 text-amber-700 border-amber-300'
        }
    };

    const color = colors[type];
    const confidenceGap = 100 - confidence;

    return (
        <div className={`bg-white rounded-lg shadow-sm border ${color.border} overflow-hidden hover:shadow-md transition-shadow`}>
            <div className="p-3.5">
                {/* Compact Header Row with Hide Button */}
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold border ${color.badge} flex-shrink-0`}>
                            {confidence}%
                        </span>
                        {confidence === 100 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 flex-shrink-0">
                                <CheckCircle2 className="w-3 h-3" />
                                Perfect
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 flex-shrink-0">
                                <AlertTriangle className="w-3 h-3" />
                                {confidenceGap}% gap
                            </span>
                        )}
                    </div>
                    {onHide && (
                        <button
                            onClick={onHide}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors border border-gray-200 flex-shrink-0"
                        >
                            <EyeOff className="w-3 h-3" />
                            Hide
                        </button>
                    )}
                </div>

                {/* Compact Offer Comparison */}
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <ContentBox label="Expected" variant="expected">
                        {databaseOffer}
                    </ContentBox>
                    <ContentBox label="Found" variant={type}>
                        {scrapedContent?.substring(0, 120) || 'Match confirmed'}
                        {scrapedContent?.length > 120 && '...'}
                    </ContentBox>
                </div>

                {issue && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                        <p className="text-xs font-medium text-amber-900 mb-0.5">Issue:</p>
                        <p className="text-xs text-amber-800">{issue}</p>
                    </div>
                )}
            </div>

            {(diagnostics || matchEvidence || pageLocation || componentAnalysis || scrapedContent) && (
                <>
                    <button
                        onClick={onToggle}
                        className="w-full px-3.5 py-2 flex items-center justify-between text-xs font-medium text-gray-700 hover:bg-gray-50 border-t border-gray-200 transition-colors"
                    >
                        <span>Additional Details</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                    </button>

                    {expanded && (
                        <div className="px-3.5 pb-3.5 space-y-3 border-t border-gray-200 bg-gray-50/50">
                            {diagnostics && (
                                <DetailSection
                                    title={confidence < 100 ? `Analysis (${confidenceGap}% gap)` : 'Match Analysis'}
                                    icon={Info}
                                >
                                    <OfferDiagnosticsDisplay diagnostics={diagnostics} />
                                </DetailSection>
                            )}

                            {matchEvidence && (
                                <DetailSection title="Match Evidence" icon={MapPin}>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-600 text-xs">Found Via:</span>
                                            <code className="px-2 py-1 bg-white rounded text-xs font-mono border border-gray-200">{matchEvidence.foundVia}</code>
                                        </div>
                                        {matchEvidence.htmlSelector && (
                                            <div className="flex items-start gap-2">
                                                <span className="text-gray-600 text-xs whitespace-nowrap">Selector:</span>
                                                <code className="px-2 py-1 bg-white rounded text-xs font-mono border border-gray-200 break-all flex-1">{matchEvidence.htmlSelector}</code>
                                            </div>
                                        )}
                                        {pageContext && (
                                            <div className="text-xs text-gray-600 pt-2 border-t border-gray-200">
                                                <div>Page: {pageContext.pageType}</div>
                                                <div>Scraped: {new Date(pageContext.scrapedAt).toLocaleString()}</div>
                                            </div>
                                        )}
                                        {matchEvidence.contextualInfo?.foundComponents && (
                                            <div className="pt-2">
                                                <span className="text-gray-600 text-xs block mb-2">Components:</span>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {matchEvidence.contextualInfo.foundComponents.map((comp, idx) => (
                                                        <span key={idx} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium border border-emerald-200">
                                                            {comp}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </DetailSection>
                            )}

                            {pageLocation && (
                                <DetailSection title="Page Location" icon={MapPin}>
                                    <div className="space-y-1 text-xs text-gray-600">
                                        <div>Type: <span className="text-gray-900">{pageLocation.elementType}</span></div>
                                        <div>Position: <span className="text-gray-900">{pageLocation.position} of {pageLocation.totalFound}</span></div>
                                        <div className="pt-1">
                                            <code className="px-2 py-1 bg-white rounded font-mono text-xs border border-gray-200 block mt-1">{pageLocation.specificSelector}</code>
                                        </div>
                                    </div>
                                </DetailSection>
                            )}

                            {componentAnalysis && !diagnostics && (
                                <DetailSection title="Component Analysis" icon={Code}>
                                    <ComponentAnalysisDisplay
                                        analysis={componentAnalysis}
                                        statusType={type === 'success' ? 'success' : 'warning'}
                                    />
                                    {extractionMethod && (
                                        <p className="text-xs text-gray-600 mt-2">Method: {extractionMethod}</p>
                                    )}
                                </DetailSection>
                            )}

                            {scrapedContent && (
                                <DetailSection title="Full Content" icon={FileText}>
                                    <pre className="text-xs bg-white p-3 rounded-lg border border-gray-200 font-mono overflow-x-auto max-h-48">
                                        {scrapedContent.substring(0, 400)}
                                        {scrapedContent.length > 400 && '...'}
                                    </pre>
                                </DetailSection>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// Content Box Component - More Compact
function ContentBox({ label, variant, children }) {
    const variants = {
        expected: 'bg-blue-50 border-blue-200 text-gray-900',
        success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
        warning: 'bg-amber-50 border-amber-200 text-amber-900'
    };

    return (
        <div>
            <div className="text-xs font-medium text-gray-500 mb-1.5">{label}</div>
            <div className={`${variants[variant]} border rounded p-2.5 text-xs leading-relaxed`}>
                {children}
            </div>
        </div>
    );
}

// Missing Offer Card - More Compact
function MissingOfferCard({ offer, onHide }) {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-3.5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-300">
                    <XCircle className="w-3 h-3" />
                    Not found
                </div>
                {onHide && (
                    <button
                        onClick={onHide}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors border border-gray-200"
                    >
                        <EyeOff className="w-3 h-3" />
                        Hide
                    </button>
                )}
            </div>

            <ContentBox label="Expected" variant="expected">
                {offer.databaseOffer}
            </ContentBox>

            {offer.expectedComponents && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded p-2.5">
                    <p className="text-xs font-medium text-gray-700 mb-2">Expected Components</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        {offer.expectedComponents.title && (
                            <div>
                                <span className="font-medium text-gray-600">Title:</span>
                                <div className="text-gray-900 mt-0.5">{offer.expectedComponents.title}</div>
                            </div>
                        )}
                        {offer.expectedComponents.price && (
                            <div>
                                <span className="font-medium text-gray-600">Price:</span>
                                <div className="text-gray-900 mt-0.5">{offer.expectedComponents.price}</div>
                            </div>
                        )}
                        {offer.expectedComponents.quality && (
                            <div>
                                <span className="font-medium text-gray-600">Quality:</span>
                                <div className="text-gray-900 mt-0.5">{offer.expectedComponents.quality}</div>
                            </div>
                        )}
                        {offer.expectedComponents.productType && (
                            <div>
                                <span className="font-medium text-gray-600">Product:</span>
                                <div className="text-gray-900 mt-0.5">{offer.expectedComponents.productType}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Hidden Offer Card - More Compact
function HiddenOfferCard({ hidden, onUnhide }) {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3.5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <ContentBox label="Hidden Offer" variant="expected">
                        {hidden.offerText}
                    </ContentBox>

                    {hidden.notes && (
                        <p className="text-xs text-gray-600 mt-2">
                            <span className="font-medium">Note:</span> {hidden.notes}
                        </p>
                    )}

                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1.5">
                        <Clock className="w-3 h-3" />
                        {new Date(hidden.hiddenAt).toLocaleString()}
                    </div>
                </div>

                {onUnhide && (
                    <button
                        onClick={onUnhide}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors border border-blue-200 flex-shrink-0"
                    >
                        <Eye className="w-3.5 h-3.5" />
                        Unhide
                    </button>
                )}
            </div>
        </div>
    );
}

// Detail Section Component - More Compact
function DetailSection({ title, icon: Icon, children }) {
    return (
        <div className="pt-3 first:pt-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-2">
                {Icon && <Icon className="w-3.5 h-3.5 text-gray-500" />}
                {title}
            </div>
            <div className="pl-5">
                {children}
            </div>
        </div>
    );
}
